import React, { Box, Grid, Typography, TextField, Button } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Delete } from '@mui/icons-material';
import { Product } from 'models';
import { ProductService } from 'services';
import { useQueryClient } from '@tanstack/react-query';
import { ProductFormProps } from './ProductFormProps';

const productSchema = z.object({
	id: z.number(),
	name: z.string().min(1, 'Please enter a product name.'),
	basePrice: z.number().min(0.01, 'Base price greater than 0.'),
	bonusPoints: z.number().min(1, 'Bonus points must be positive.'),
});

export const ProductForm = ({ product }: ProductFormProps) => {
	const isNew = (product?.id ?? 0) === 0;
	const queryClient = useQueryClient();
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<Product>({
		defaultValues: !isNew ? product : {
			id: 0,
			name: '',
			basePrice: 0,
			bonusPoints: 0
		},
		resolver: zodResolver(productSchema),
		mode: 'onBlur',
	});

	const onSubmit = async (data: Product) => {
		if (isNew) {
			await ProductService.create(data);
		} else {
			await ProductService.update(data);
		}
		reset();
		queryClient.invalidateQueries({ queryKey: ['products'] });
	};

	const handleDeleteProduct = async () => {
		await ProductService.delete(product?.id ?? 0);
		reset();
		queryClient.invalidateQueries({ queryKey: ['products'] });
	};

	return (
		<Box sx={{ margin: 2 }} component='form' onSubmit={handleSubmit(onSubmit)}>
			<Grid container spacing={2} pt={2}>
				<Typography variant='h6' gutterBottom component='div'>
					{`${isNew ? 'Create' : 'Edit'} Product`}
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Controller
							name='name'
							control={control}
							render={({ field }) => <TextField {...field} label='Name' placeholder='Enter product name' error={!!errors.name} helperText={errors.name?.message} fullWidth />}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name='basePrice'
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									onChange={e => field.onChange(parseFloat(e.target.value))}
									label='Base Price'
									type='number'
									placeholder='Enter product price'
									error={!!errors.basePrice}
									helperText={errors.basePrice?.message}
									fullWidth
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name='bonusPoints'
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									onChange={e => field.onChange(parseFloat(e.target.value))}
									label='Bonus Points'
									type='number'
									placeholder='Enter bonus points'
									error={!!errors.bonusPoints}
									helperText={errors.bonusPoints?.message}
									fullWidth
								/>
							)}
						/>
					</Grid>
					{isNew ? (
						<Grid item xs={12}>
							<Button type='submit' variant='contained' color='primary' fullWidth>
								Create
							</Button>
						</Grid>
					) : (
						<>
							<Grid item xs={10}>
								<Button type='submit' variant='contained' color='primary' fullWidth>
									Update
								</Button>
							</Grid>
							<Grid item xs={2}>
								<Button variant='contained' color='error' fullWidth onClick={handleDeleteProduct}>
									<Delete />
								</Button>
							</Grid>
						</>
					)}
				</Grid>
			</Grid>
		</Box>
	);
}
